import { useQuery } from "@tanstack/react-query";
import { categoriesApi, type CategoryDto } from "@/api/lookups";

/**
 * Admin-managed Lookups v1.0 §4d — single source of truth for the
 * user-facing active category list. Replaces the hardcoded
 * <c>TemplateCategory</c> enum on the Report Template editor and the
 * category-badge rendering on the Report Templates list view.
 *
 * Five-minute staleTime per spec §8. Admin mutations on
 * <c>/admin/categories</c> invalidate the <c>['categories']</c> key.
 */
export function useCategories() {
  return useQuery({
    queryKey: ["categories"],
    queryFn: () => categoriesApi.list(),
    staleTime: 5 * 60_000,
  });
}

export function useAdminCategories(includeArchived: boolean) {
  return useQuery({
    queryKey: ["admin-categories", includeArchived],
    queryFn: () => categoriesApi.adminList(includeArchived),
  });
}

export function useCategoryLabel(code: string): {
  isLoading: boolean;
  label: string;
  category: CategoryDto | undefined;
} {
  const { data, isLoading } = useCategories();
  const category = data?.find((c) => c.code === code);
  return {
    isLoading,
    label: category?.displayName ?? code,
    category,
  };
}

export function useCategoryLabels(): {
  isLoading: boolean;
  titleOf: (code: string) => string | undefined;
} {
  const { data, isLoading } = useCategories();
  const lookup = data?.reduce<Record<string, string>>((acc, c) => {
    acc[c.code] = c.displayName;
    return acc;
  }, {}) ?? {};
  return {
    isLoading,
    titleOf: (code) => lookup[code],
  };
}
